import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../../config";
import { debounce } from "lodash";
import MainBody from "../../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../../components/Pagination";
import sortCaret from "../../../../components/SortCaret";
import { ResponsiveDrawerForm } from "../../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import AddEditTraining from "./AddEditTraining";
import TrainingActionButton from "./TrainingActionButton";
import Confirmation from "../../../../components/Confirmation";
import AdminFilters from "../../../../components/AdminFilters";
import { Button } from "../../../../components/ui/button";
import Assessments from "./Assessments";
import { GearIcon } from "@radix-ui/react-icons";
import { AnimatedTooltip } from "../../../../components/AnimatedTooltip";

const Trainings = ({ expandList = false, trainingData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [trainingList, setTrainingsList] = useState(
    expandList ? trainingData : [],
  );

  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [trainingId, setTrainingId] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [showAddEditTraining, setShowAddEditTraining] = useState(false);
  const [showTrainingDelete, setShowTrainingDelete] = useState(false);
  const [organizationTypeId, setOrganizationTypeId] = useState();

  // const [showAddEditAssessment, setShowAddEditAssessment] = useState(false);

  const [showView, setShowView] = useState(false);

  const toggleShowModal = () => {
    setShowView(!showView);
  };

  // const toggleShowAddEditAssessment = () => {
  //   if (showAddEditAssessment) {
  //     setEdit(false);
  //     setEditItem(null);
  //     setTrainingId(null);
  //   }
  //   setShowAddEditAssessment(!showAddEditAssessment);
  // };

  const toggleShowDelete = () => {
    if (showTrainingDelete) {
      setTrainingId(null);
    }
    setShowTrainingDelete(!showTrainingDelete);
  };

  const toggleShowAddEditTraining = () => {
    if (showAddEditTraining) {
      setEdit(false);
      setEditItem(null);
      setTrainingId(null);
    }
    setShowAddEditTraining(!showAddEditTraining);
  };

  const fetchTrainings = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_TRAININGS}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }&organizationType_id=${organizationTypeId ? organizationTypeId : ""}`,
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const trainingList = data.data.data;
        setAssessmentData(data?.assessment);
        setTrainingsList(trainingList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setTrainingsList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortField, sortOrder, organizationTypeId],
  );

  const trainingsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: trainingList[i]?.id,
        id: trainingList[i]?.id,
        organization_type: (
          <AnimatedTooltip
            leftAlign={false}
            title={
              <ul>
                {trainingList[i]?.organization_types
                  ?.filter((item) => item?.name)
                  .map((item, index) => (
                    <li key={index}>{item.name}</li>
                  ))}
              </ul>
            }
          >
            <span>
              {trainingList[i]?.organization_types?.length} Industry Types
            </span>
          </AnimatedTooltip>
        ),
        order: trainingList[i]?.order ? trainingList[i]?.order : "",
        title: trainingList[i]?.title ? trainingList[i]?.title : "",
        description: trainingList[i]?.description
          ? trainingList[i]?.description
          : "",
        link: trainingList[i]?.link ? trainingList[i]?.link : "",
        action: (
          <>
            <TrainingActionButton
              id={trainingList[i]?.id}
              setEdit={setEdit}
              setTrainingId={setTrainingId}
              toggleShowAddEditTraining={toggleShowAddEditTraining}
              toggleShowDelete={toggleShowDelete}
            />
          </>
        ),
      });
    }
    return items;
  };

  const trainings = trainingsGenerator(trainingList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "organization_type",
      text: "Industry Type",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "order",
      text: "Order",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: `${THClasses} !max-w-[220px] !whitespace-normal`,
      classes: `${TDClasses} !max-w-[220px] !whitespace-normal`,
      dataField: "title",
      text: "Title",
    },
    {
      headerClasses: `${THClasses} !max-w-[220px] !whitespace-normal`,
      classes: `${TDClasses} !max-w-[220px] !whitespace-normal`,
      dataField: "description",
      text: "Description",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "link",
      text: "Link",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC",
      );
    }
  };

  useEffect(() => {
    if (!expandList) {
      navigate(
        `?tab=trainings&limit=${limit ? limit : defaultLimitPagination}&page=${
          page ? page : 1
        }&search=${searchParam ? searchParam : ""}&sort_column=${
          sortField ? sortField : ""
        }`,
        {
          replace: true,
        },
      );
    }
  }, [navigate, limit, searchParam, page, sortField, sortOrder, expandList]);

  useEffect(() => {
    if (!expandList) {
      fetchTrainings();
    }
  }, [expandList, fetchTrainings]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    [],
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchTrainings(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchTrainings(limit, page);
  };

  // Single Training Delete
  const handleTrainingDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.ADMIN_TRAINING_DELETE}/${trainingId}`);
      setLoading(false);
      fetchTrainings();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        search={!expandList}
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          !expandList && (
            <div className="flex items-center gap-3 flex-wrap">
              <AdminFilters
                organizationType={true}
                setOrganizationTypeId={setOrganizationTypeId}
                organizationTypeId={organizationTypeId}
                version={false}
              />

              {/* {assessmentData?.length > 0 && ( */}
              <Button
                onClick={() => {
                  toggleShowModal();
                }}
              >
                <div className="flex items-center gap-1">
                  <GearIcon size={18} />
                  <span>Manage Assessment</span>
                </div>
              </Button>
              {/* )} */}
              {showView && (
                <ResponsiveDrawerForm
                  description={""}
                  title={"Manage Assessment"}
                  handleOpen={() => {}}
                  handleClose={() => {}}
                  className={"md:w-[98w]"}
                  open={showView}
                  buttonClassName={""}
                  setOpen={setShowView}
                  read={true}
                >
                  <>
                    <Assessments
                      trainingList={assessmentData}
                      fetchTrainings={fetchTrainings}
                      limit={limit}
                      page={page}
                    />
                  </>
                </ResponsiveDrawerForm>
              )}

              {/* Add Assessment */}
              {/* <ResponsiveDrawerForm
                description={""}
                title={"Add Assessment"}
                handleOpen={() => {}}
                handleClose={() => {
                  setEdit(false);
                  setTrainingId();
                  setEditItem(null);
                }}
                buttonText={
                  <div className="flex items-center gap-1">
                    <LucidePlusCircle size={18} />
                    <span>
                      {assessmentData?.question?.length > 0 ? "Edit" : "Add"}{" "}
                      Assessment
                    </span>
                  </div>
                }
                open={showAddEditAssessment}
                buttonClassName={""}
                setOpen={setShowAddEditAssessment}
                mobileCancelHidden={true}
                className={"sm:max-w-5xl"}
              >
                <AddEditTraining
                  edit={assessmentData?.id ? true : false}
                  editItem={editItem}
                  assessment={true}
                  toggleShowAddEditTraining={toggleShowAddEditAssessment}
                  id={assessmentData?.id}
                  fetchTrainingData={() => fetchTrainings(limit, page)}
                />
              </ResponsiveDrawerForm> */}

              {/* Add Training */}
              <ResponsiveDrawerForm
                description={""}
                title={edit ? "Edit Training(s)" : "Add Training(s)"}
                handleOpen={() => {}}
                handleClose={() => {
                  setEdit(false);
                  setTrainingId();
                  setEditItem(null);
                }}
                buttonText={
                  <div className="flex items-center gap-1">
                    <LucidePlusCircle size={18} />
                    <span>Add Training</span>
                  </div>
                }
                open={showAddEditTraining}
                buttonClassName={""}
                setOpen={setShowAddEditTraining}
                mobileCancelHidden={true}
                className={"sm:max-w-5xl"}
              >
                <AddEditTraining
                  edit={edit}
                  editItem={editItem}
                  assessment={false}
                  toggleShowAddEditTraining={toggleShowAddEditTraining}
                  id={trainingId}
                  fetchTrainingData={() => fetchTrainings(limit, page)}
                />
              </ResponsiveDrawerForm>
            </div>
          )
        }
        loading={loading}
        innerBody={true}
        expandList={true}
        mainClassName="w-[calc(100%-3rem)]"
      >
        <div className="custom-table">
          <div className="table-body-class max-h-[590px]">
            <BootstrapTable
              keyField="key"
              remote
              data={trainings}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
          <Confirmation
            title={"Delete Training"}
            open={showTrainingDelete}
            setOpen={setShowTrainingDelete}
            toggleShow={() => {
              toggleShowDelete();
            }}
            className="sm:max-w-1/2"
            message={"Are you sure you want to delete training?"}
            okAction={async () => {
              handleTrainingDelete();
              toggleShowDelete();
              fetchTrainings();
            }}
            cancelAction={() => {
              setTrainingId(null);
              setShowTrainingDelete(!showTrainingDelete);
            }}
          />
        </div>
      </MainBody>
    </>
  );
};

export default Trainings;
